import { PASSWORD_MIN_LENGTH, PASSWORD_RULES, validatePassword } from "@edm/shared";
import { zodResolver } from "@hookform/resolvers/zod";
import { Link, useNavigate } from "@tanstack/react-router";
import { CheckCircle2, Circle, KeyRound } from "lucide-react";
import { useState } from "react";
import { useForm } from "react-hook-form";
import { toast } from "sonner";
import { z } from "zod";
import { Alert } from "@/components/ui/alert";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { ApiError } from "@/lib/api";
import { useChangePassword, useMe } from "@/lib/auth";

const formSchema = z
  .object({
    currentPassword: z.string().min(1, "请输入当前密码"),
    newPassword: z.string().min(PASSWORD_MIN_LENGTH, `密码至少 ${PASSWORD_MIN_LENGTH} 个字符`),
    confirmPassword: z.string().min(1, "请再次输入新密码"),
  })
  .superRefine((value, ctx) => {
    const result = validatePassword(value.newPassword);
    if (!result.ok) {
      ctx.addIssue({
        code: "custom",
        path: ["newPassword"],
        message: `密码还需满足：${result.unmet.join("、")}`,
      });
    }
    if (value.newPassword !== value.confirmPassword) {
      ctx.addIssue({ code: "custom", path: ["confirmPassword"], message: "两次输入的密码不一致" });
    }
  });

type FormValues = z.infer<typeof formSchema>;

export function ChangePasswordPage() {
  const navigate = useNavigate();
  const { data: me } = useMe();
  const change = useChangePassword();
  const [error, setError] = useState<string | null>(null);
  const form = useForm<FormValues>({
    resolver: zodResolver(formSchema),
    defaultValues: { currentPassword: "", newPassword: "", confirmPassword: "" },
  });
  const password = form.watch("newPassword");
  const forced = Boolean(me?.user.mustChangePassword);

  const onSubmit = form.handleSubmit(async (values) => {
    setError(null);
    try {
      await change.mutateAsync({
        currentPassword: values.currentPassword,
        newPassword: values.newPassword,
      });
      toast.success("密码已更新");
      navigate({ to: "/", replace: true });
    } catch (err) {
      setError(err instanceof ApiError ? err.message : "密码修改失败，请稍后重试");
    }
  });

  return (
    <div className="mx-auto flex min-h-[70vh] max-w-2xl items-center">
      <Card className="w-full">
        <CardHeader>
          <div className="mb-2 flex h-10 w-10 items-center justify-center rounded-xl bg-primary/10 text-primary">
            <KeyRound className="h-5 w-5" />
          </div>
          <CardTitle>{forced ? "首次登录改密" : "修改密码"}</CardTitle>
          <CardDescription>
            {forced
              ? "为保障账号安全，首次登录必须设置新密码后才能进入系统。"
              : "修改后，其他设备上的登录会话将立即失效。"}
          </CardDescription>
        </CardHeader>
        <CardContent>
          {error ? (
            <Alert variant="danger" className="mb-5" title="修改未完成">
              {error}
            </Alert>
          ) : null}
          <form className="space-y-5" onSubmit={onSubmit} noValidate>
            <div className="space-y-2">
              <Label htmlFor="currentPassword">当前密码</Label>
              <Input
                id="currentPassword"
                type="password"
                autoComplete="current-password"
                {...form.register("currentPassword")}
              />
              {form.formState.errors.currentPassword ? (
                <p className="text-xs text-red-600">
                  {form.formState.errors.currentPassword.message}
                </p>
              ) : null}
            </div>

            <div className="space-y-2">
              <Label htmlFor="newPassword">新密码</Label>
              <Input
                id="newPassword"
                type="password"
                autoComplete="new-password"
                {...form.register("newPassword")}
              />
              <ul className="grid gap-1.5 text-xs text-muted-foreground sm:grid-cols-2">
                {PASSWORD_RULES.map((rule) => {
                  const met = rule.test(password);
                  return (
                    <li
                      key={rule.code}
                      className={
                        met
                          ? "flex items-center gap-1.5 text-emerald-700"
                          : "flex items-center gap-1.5"
                      }
                    >
                      {met ? (
                        <CheckCircle2 className="h-3.5 w-3.5" />
                      ) : (
                        <Circle className="h-3.5 w-3.5" />
                      )}
                      {rule.label}
                    </li>
                  );
                })}
              </ul>
              {form.formState.errors.newPassword ? (
                <p className="text-xs text-red-600">{form.formState.errors.newPassword.message}</p>
              ) : null}
            </div>

            <div className="space-y-2">
              <Label htmlFor="confirmPassword">确认新密码</Label>
              <Input
                id="confirmPassword"
                type="password"
                autoComplete="new-password"
                {...form.register("confirmPassword")}
              />
              {form.formState.errors.confirmPassword ? (
                <p className="text-xs text-red-600">
                  {form.formState.errors.confirmPassword.message}
                </p>
              ) : null}
            </div>

            <div className="flex flex-col gap-2 sm:flex-row sm:justify-end">
              {!forced ? (
                <Button asChild variant="outline" type="button">
                  <Link to="/">取消</Link>
                </Button>
              ) : null}
              <Button type="submit" disabled={change.isPending}>
                {change.isPending ? "正在保存…" : "保存并进入系统"}
              </Button>
            </div>
          </form>
        </CardContent>
      </Card>
    </div>
  );
}
